import { useEffect, useRef, useState, useCallback } from "react";
import { Button, Input, Modal, Space, Tooltip } from "antd";
import TableWithStaticPagination from "@pages/empi/components/table/tableWithStaticPagination";
import { useDataQualityReportStore } from "@stores/dataQualityReport";
import WellBeEMPIService from "@services/empiService.service";
import { map, filter, sortBy, get, cloneDeep } from "lodash";
import { getProgressColorCode } from "@pages/utils/utils";
import "./tableView.scss";
import { SearchOutlined } from "@ant-design/icons";

const TableView = (props?: any) => {
  const { height } = props;
  const [loading, setLoading] = useState(false);
  const [tableColumn, setTableColumn] = useState<any>([]);
  const dataDetails = useDataQualityReportStore((state) => state.dataDetails);
  const selectedScoreType = useDataQualityReportStore((state) => state.selectedScoreType);
  const loadingForCardOrTableView = useDataQualityReportStore((state) => state.loadingForCardOrTableView);
  const [tablePage, setTablePage] = useState(1);
  const [columnSearchInfo, setColumnSearchInfo] = useState<any>({});
  const [data, setData] = useState<any>(dataDetails);

  // use the data state in store which we updated in the score board here to draw the table.
  // const columnsList: any = useRef(null);
  const inputref = useRef<any>(null);

  useEffect(() => setData(dataDetails), [dataDetails]);
  useEffect(() => {
    setTablePage(1);
    setColumnSearchInfo({});
  }, [tableColumn, selectedScoreType?.id, props.loader]);

  const fetchTableColumns = useCallback(() => {
    setColumnSearchInfo({});
    setData(dataDetails);
    const pageName =
      selectedScoreType?.id === "Completeness" ? "Data-Quality-Completeness-Score" : selectedScoreType?.id === "Conformity" ? "Data-Quality-Conformity-Score-limited" : "Data-Quality-Uniqueness-Score";
    setLoading(true);
    WellBeEMPIService.getColumnsList({ pageName }).subscribe({
      next: (v) => {
        setTableColumn(
          filter(v.data.data.columns, (item) => {
            return item.isDisplay === "Y";
          })
        );
        setLoading(false);
      },
      error: (e) => {
        console.log(e);
        setLoading(false);
      },
    });
  }, [setTableColumn, selectedScoreType?.id]);

  useEffect(() => {
    fetchTableColumns();
  }, [selectedScoreType?.id, fetchTableColumns]);

  const onTablePageChange = (page: any, pageSize: any) => {
    setTablePage(page);
  };

  const applyColumnSearch = (columnName: any, text: any) => {
    text = text ? text : get(columnSearchInfo, [columnName], "");
    if (text !== "") {
      setData(
        dataDetails.filter((data: any) => {
          if (data[columnName]) {
            return data[columnName].toString().toLowerCase().includes(text.toLowerCase());
          } else {
            return false;
          }
        })
      );
    } else {
      setData(dataDetails);
    }
  };

  const columns: any = tableColumn?.map((item: any) => {
    let temp = cloneDeep(item);
    if (item.key === "file_name" || item.key === "payer_name" || item.key === "file_category") {
      temp = {
        title: item.tableHeaderName,
        dataIndex: item.dataIndex,
        key: item.key,
        ellipsis: true,
        ...(item.key === "file_name" ? { width: 320 } : {}),
        filterDropdown: ({ confirm }: any) => (
          <div style={{ padding: "10px" }}>
            <Input
              ref={inputref}
              style={{ marginBottom: 8, display: "block" }}
              value={get(columnSearchInfo, item.key, "")}
              onChange={(e) => {
                let temp = {
                  ...columnSearchInfo,
                  [item.key]: e.target.value,
                };
                console.log(temp);
                setColumnSearchInfo(temp);
              }}
              onPressEnter={(e: any): any => {
                applyColumnSearch(item.key, "");
                confirm({ closeDropdown: true });
                e.stopPropagation();
              }}
            />
            <Space>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                size="small"
                style={{ width: 90 }}
                onClick={(e) => {
                  setColumnSearchInfo({
                    [item.key]: get(columnSearchInfo, item.key, ""),
                  });
                  applyColumnSearch(item.key, "");
                  confirm({ closeDropdown: true });
                }}
              >
                Search
              </Button>
              <Button
                onClick={() => {
                  setColumnSearchInfo({ [item.key]: "" });
                  applyColumnSearch("", "");
                  confirm({ closeDropdown: true });
                }}
                size="small"
                style={{ width: 90 }}
              >
                Reset
              </Button>
            </Space>
          </div>
        ),
        filterIcon:
          columnSearchInfo[item?.key] === ""
            ? null
            : () => (
                <SearchOutlined
                  style={{
                    color: get(columnSearchInfo, item.key, "") !== "" ? "#EF6D2A" : undefined,
                  }}
                />
              ),
        sorter: (a: any, b: any) => {
          return a?.[item?.key]?.localeCompare(b?.[item?.key]);
        },
        render:
          item.key === "file_name" && selectedScoreType?.id !== "Conformity"
            ? (value: any, record: any) => {
                return <ExtraDetailsModal1 record={record} value={value} />;
              }
            : (value: any) => value || "--",
      };
    } else if (item.key === "actual_value") {
      temp = {
        title: item.tableHeaderName,
        dataIndex: item.dataIndex,
        key: item.key,
        ellipsis: true,
        render: (value: any, record: any) => {
          return (
            <span
              style={{
                color: getProgressColorCode(record?.color_code),
              }}
            >
              {`${value}` || "--"}
            </span>
          );
        },
      };
    } else if (item.key === "dq_category_score") {
      temp = {
        ellipsis: true,
        title: item.tableHeaderName,
        dataIndex: item.dataIndex,
        key: item.key,
        render: (value: any) => value ? `${value}%` : value === 0 ? `${value}%` : "--",
      };
    } else {
      temp = {
        ellipsis: true,
        title: item.tableHeaderName,
        dataIndex: item.dataIndex,
        key: item.key,
        render: (value: any) => value || "--",
      };
    }
    return temp;
  });

  return (
    <TableWithStaticPagination
      height={height}
      pagination={{ position: "bottomRight", current: tablePage, onChange: onTablePageChange, showSizeChanger: true }}
      columns={columns}
      data={data}
      loading={loading || loadingForCardOrTableView}
    />
  );
};

const ExtraDetailsModal1 = ({ record, value }: any) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const selectedScoreType = useDataQualityReportStore((state) => state.selectedScoreType);
  const failedFilesType = useDataQualityReportStore((state) => state.failedFilesType);

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleOk = () => {
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };
  return (
    <>
      <span
        // style={{
        //   color: getProgressColorCode(record?.color_code),
        // }}
        style={{
          color: "#0072e3",
        }}
        onClick={showModal}
        className="pointer"
      >
        {value || "--"}
      </span>

      <Modal
        width={"43%"}
        footer={<div style={{ textAlign: "left", color: "red"}}>* Red values indicate below-threshold attributes.</div>}
        title={`Score Information | ${record.payer_name} | ${record?.file_category} | ${record?.file_name} | ${selectedScoreType?.label}`}
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        wrapClassName="ui-data-quality-score-modal"
      >
        <div className="ui-data-quality-score-modal__table">
          <div className="ui-data-quality-score-modal__table__header">
            <div className="ui-data-quality-score-modal__table__header__column">Attribute</div>
            <div className="ui-data-quality-score-modal__table__header__column">Score</div>
            <div className="ui-data-quality-score-modal__table__header__column">Actual Value</div>
            <div className="ui-data-quality-score-modal__table__header__column">Threshold</div>
            {/* <div className="ui-data-quality-score-modal__table__header__column">Upper Limit</div> */}
          </div>
          <div className="ui-data-quality-score-modal__table__container">
            {map(record?.additionalInfo, (item) => {
              return (
                <div key={item?.dataIndex} className="ui-data-quality-score-modal__table__body">
                  <Tooltip title={item?.dq_rule_attribute_alias?.length > 7 ? item?.dq_rule_attribute_alias : null}>
                    <div className="ui-data-quality-score-modal__table__body__column ellipsis">{item?.dq_rule_attribute_alias}</div>
                  </Tooltip>

                  <div
                    className="ui-data-quality-score-modal__table__body__column"
                    style={{
                      color: getProgressColorCode(item?.dq_attribute_score_color),
                    }}
                  >
                    {`${item?.dq_attribute_score}%` || "--"}
                  </div>
                  <div className="ui-data-quality-score-modal__table__body__column">{item?.actual_value || "--"}</div>
                  <div className="ui-data-quality-score-modal__table__body__column">{item?.threshold_value || "--"}</div>
                  {/* <div className="ui-data-quality-score-modal__table__body__column">{item?.expected_value?.ub || "--"}</div> */}
                </div>
              );
            })}
          </div>
        </div>
      </Modal>
    </>
  );
};

export default TableView;
